/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {GlobalScenario} from '../../../models/global-scenario-dto.model';
import {PivotTableVehDataRequestDTO} from '../../../models/pivot-table-veh-data-request-dto.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {MarketAreaService} from '../../../services/market-area.service';
import {PlanningMarketService} from '../../../services/planning-market.service';
import {SubregionService} from '../../../services/subregion.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-pivot-table-data-vehicle',
    templateUrl: './pivot-table-data-vehicle.component.html',
    styleUrl: './pivot-table-data-vehicle.component.scss',
    standalone: false
})
export class PivotTableDataVehicleComponent implements OnInit {

  showSpinner: boolean;
  selectedScenarioSource: number;

  subregionFlag: number;
  marketAreas = new SelectableOptions();
  subregions = new SelectableOptions();
  planningMarkets = new SelectableOptions();
  getPortLevelData: number;
  getPropulsionSegment: boolean;

  startYear: number;
  endYear: number;
  yearRange: number[];

  includeCompare: boolean;
  selectedScenario: VehicleScenario;
  compareScenario: VehicleScenario;
  selectedGlobalScenario: GlobalScenario;
  compareGlobalScenario: GlobalScenario;

  constructor(readonly vehicleScenarioService: VehicleScenarioService,
              readonly subregionService: SubregionService,
              readonly marketAreaService: MarketAreaService,
              readonly planningMarketService: PlanningMarketService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  async ngOnInit() {
    this.showSpinner = false;
    this.selectedScenarioSource = 0;
    this.includeCompare = false;
    this.getPortLevelData = 1;
    this.getPropulsionSegment = true;
    this.subregionFlag = 1;

    if (this.stateService.isVehicleScenarioSelected) {
      this.selectedScenario = this.stateService.selectedScenario === null ? null : this.stateService.selectedScenario;
      this.compareScenario = this.stateService.selectedCompareScenario === null ? null : this.stateService.selectedCompareScenario;
    }
    if (this.selectedScenario) {
      await this.getGeography();
    }

    this.getYearRange();
  }

  /**
   * fetches the market areas and subregions for a scenario
   */
  async getGeography() {
    this.showSpinner = true;
    try {
      this.marketAreas.options = await this.marketAreaService.getMarketAreasByVehicleScenario(this.selectedScenario.vehScenId, this.stateService.getUserGmid()).toPromise();
      this.subregions.options = await this.subregionService.getSubregionsByVehicleScenario(this.selectedScenario.vehScenId, this.stateService.getUserGmid()).toPromise();
      this.planningMarkets.options = await this.planningMarketService.getPlanningMarketsByVehicleScenario(this.stateService.getUserGmid(), this.selectedScenario.vehScenId).toPromise();
    } catch (error) {
      this.snackBar.displayError(error, DISMISS_MSG);
      this.showSpinner = false;
    }
    this.showSpinner = false;
  }

  /**
   * Gets either the default year range or the year range of the selected scenario
   */
  getYearRange() {
    if ((this.selectedScenario !== null && this.selectedScenario !== undefined) && this.selectedScenarioSource === 0) {
      this.getScenarioYearRange();
    } else {
      this.getDefaultYearRange();
    }
  }


  /**
   * Gets the default year range for the reports
   */
  getDefaultYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * gets the year range if there is a vehicle scenario selected
   */
  getScenarioYearRange() {
    this.showSpinner = true;
    this.vehicleScenarioService.getYearRange(this.selectedScenario.vehScenId).subscribe({
      next: data => {
        if (data && data.length > 0) {
          // If the default year range is a valid range of volumes in the scenario, then use the default year range. else use the year range values from the scenario
          const scenarioMinYear = data[0].MIN_YEAR;
          const scenarioMaxYear = data[0].MAX_YEAR;
          const {startYear, endYear} = getDefaultYearRange();
          if (startYear < scenarioMaxYear) {
            this.startYear = Math.max(scenarioMinYear, startYear);
            this.endYear = Math.min(endYear, scenarioMaxYear);
            this.yearRange = range(Math.min(scenarioMinYear, startYear), Math.max(endYear, scenarioMaxYear) + 1);
          } else {
            this.startYear = scenarioMinYear;
            this.endYear = scenarioMaxYear;
            this.yearRange = range(this.startYear, this.endYear + 1);
          }
        }
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
      }
    })
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  selectScenario = (): void => {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(async res => {
      if (res) {
        this.selectedScenario = res.selectedScenarios[0];
        this.compareScenario = this.selectedScenario;
        this.getYearRange();
        await this.getGeography();
      }
    });
  }


  /**
   * Opens the SelectCompareScenario pop up component
   */
  selectCompareScenario(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.selectedScenario.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.compareScenario = res.selectedScenarios[0];
      }
    });
  }

  /**
   * Event handler for when a working global scenario is selected
   * @param data
   */
  onGlobalScenarioSelect(data: any): void {
    this.selectedGlobalScenario = data.globalScenario;
  }

  /**
   * Event handler for when a compare global scenario is selected
   * @param data
   */
  onCompareGlobalScenarioSelect(data: any): void {
    this.compareGlobalScenario = data.globalScenario;
  }

  /**
   * Event handler for when the compare checkbox is checked
   */
  onCompareChange() {
    this.compareGlobalScenario = null;
  }

  /**
   * Event handler for when the geography type changes
   */
  onGeographyChange() {
    this.marketAreas.selectedOptions = []
    this.subregions.selectedOptions = [];
  }

  /**
   * Event handler for when the scenario source changes
   */
  onSourceChange() {
    this.selectedGlobalScenario = null;
    this.compareGlobalScenario = null;
    this.marketAreas.selectedOptions = []
    this.subregions.selectedOptions = [];
    if (this.selectedScenarioSource === 1) {
      this.subregionFlag = 1;
    }
    this.getYearRange();
  }

  /**
   * Method that gets the pivot table data
   */
  onRunReport() {
    let scenarioId: number, compareId: number, markets: string[]
    if (this.selectedScenarioSource === 0) {
      scenarioId = this.selectedScenario.vehScenId;
      compareId = this.includeCompare ? this.compareScenario.vehScenId : -1;
      switch (this.subregionFlag) {
        case 0:
          markets = this.marketAreas.selectedOptions
          break;
        case 1:
          markets = this.subregions.selectedOptions;
          break;
        case 2:
          markets = this.planningMarkets.selectedOptions;
          break;
      }
    } else {
      scenarioId = this.selectedGlobalScenario.id;
      compareId = this.includeCompare ? this.compareGlobalScenario.id : -1;
      markets = [];
    }

    if (scenarioId === compareId) {
      this.snackBar.displayError('Working and compare scenario IDs are the same', DISMISS_MSG);
      return;
    }

    const requestDTO: PivotTableVehDataRequestDTO = {
      vehScenId: scenarioId,
      compareScenId: compareId,
      getPropSeg: this.getPropulsionSegment,
      globalFlag: this.selectedScenarioSource,
      gmid: this.stateService.getUserGmid(),
      marketFlag: this.subregionFlag,
      markets,
      portDetailFlag: this.getPortLevelData,
      startYear: this.startYear,
      endYear: this.endYear
    }

    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const scenarioString = this.includeCompare ?
      scenarioId + "-" + compareId : scenarioId;
    const fileName = 'VehPivotData_' + scenarioString + "_" + currDate;
    this.showSpinner = true;
    this.vehicleScenarioService.getPivotTableData(requestDTO).subscribe({
      next: (response: any) => {
        this.excelService.exportToExcelFromServer(response, fileName);
      },
      error: (error: any) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
      }
    });
  }

  /**
   * Method that decides the state of the confirm button
   */
  isButtonEnabled(): boolean {
    if (this.selectedScenarioSource === 0) {
      if (this.includeCompare) {
        return this.selectedScenario && this.compareScenario
          && (this.marketAreas.selectedOptions.length > 0 || this.subregions.selectedOptions.length > 0 || this.planningMarkets.selectedOptions.length > 0);
      } else {
        return this.selectedScenario
          && (this.marketAreas.selectedOptions.length > 0 || this.subregions.selectedOptions.length > 0 || this.planningMarkets.selectedOptions.length > 0);
      }
    } else {
      if (this.includeCompare) {
        return (Boolean(this.selectedGlobalScenario) && Boolean(this.compareGlobalScenario));
      } else {
        return Boolean(this.selectedGlobalScenario);
      }
    }
  }
}
